import type { ReactNode } from 'react';
import {
  AppHomeContentResponseModelContentType,
  AppProfileContentResponseModelContentType,
  BenefitsScreenContentResponseModelContentType,
  BookFlowEatAndDrinkContentResponseModelContentType,
  BookFlowEventContentResponseModelContentType,
  BookFlowExperiencesContentResponseModelContentType,
  BookFlowParkingContentResponseModelContentType,
  BookFlowRangeRoverContentResponseModelContentType,
  BookFlowStayContentResponseModelContentType,
  BookFlowSunbedContentResponseModelContentType,
  BookIntroContentResponseModelContentType,
  CheckoutContentResponseModelContentType,
  EventsCalendarContentResponseModelContentType,
  ExploreIntroContentResponseModelContentType,
  ShopIntroContentResponseModelContentType,
  ShopOrderConfirmationContentResponseModelContentType,
  ShopProductDetailContentResponseModelContentType,
  ShopSectionContentResponseModelContentType,
  ShopDiscoverContentResponseModelContentType,
  LocationDiscoverContentResponseModelContentType,
  type ApiBlockListModel,
  type CheckoutContentResponseModel,
  type ContentPropertiesModel,
  type ExploreIntroContentResponseModel,
  type IApiContentResponseModel,
  type ShopIntroContentResponseModel,
  type ShopOrderConfirmationContentResponseModel,
  type ShopSectionContentResponseModel,
} from '@/services/platform/api/model';
import { AppContext } from '@/lib/core/types';
import { ModulesRow } from '@/components/modules/row';
import { ProfileIntro } from '@/components/shared/profile/intro';
import { BenefitsPage } from '@/components/shared/profile/benefits';
import { BookingEngine } from '@/components/shared/booking/engine';
import { ExploreIntroScreen } from '@/components/shared/explore/intro';
import { ExploreMapScreen } from '@/components/shared/explore/map';
import { EventsCalendarScreen } from '@/components/shared/events';
import {
  ShopIntroScreen,
  ShopOrderConfirmedScreen,
  ShopProductDetailScreen,
  ShopSectionIntroScreen,
} from '@/components/shared/shop';
import { ShopDiscover } from '@/components/shared/shop/discover';
import { CheckoutScreenRouter } from '@/components/shared/checkout';
import { ScreenHead } from '@/components/shared/screen/head';
import { useClientTranslation } from '@/lib/core/i18n';
import { getSectionFromPath, useShopSections } from '@/lib/core/shop/sections';
import { cn, PageMode } from '@/lib/core/utils';
import { useUIStore } from '@/lib/core/stores/useUIStore';

interface ScreenRendererArgs {
  content: IApiContentResponseModel;
  context?: AppContext;
  fullPath?: string;
}

type ScreenRenderer = (args: ScreenRendererArgs) => ReactNode;

const delegateToBookingEngine: ScreenRenderer = ({ content, context }) => (
  <BookingEngine content={content} context={context} />
);

/**
 * Maps `content.contentType` → render function. Mirrors the dispatch pattern
 * used by `BookingEngine` ([booking/engine/index.tsx]).
 */
export const screenContentTypeMap: Record<string, ScreenRenderer> = {
  [AppHomeContentResponseModelContentType.appHome]: ({ content, context }) => (
    <div className="relative w-full">
      <ModulesRow
        components={
          (content.properties as ContentPropertiesModel)?.components as ApiBlockListModel | undefined
        }
        context={context}
      />
    </div>
  ),

  [AppProfileContentResponseModelContentType.appProfile]: ({ content, context }) => (
    <ProfileIntro content={content} context={context} />
  ),

  [BenefitsScreenContentResponseModelContentType.benefitsScreen]: ({ content }) => (
    <BenefitsPage content={content} />
  ),

  [ExploreIntroContentResponseModelContentType.exploreIntro]: ({ content }) => (
    <ExploreIntroScreen content={content as ExploreIntroContentResponseModel} />
  ),

  [LocationDiscoverContentResponseModelContentType.locationDiscover]: () => <ExploreMapScreen />,

  [EventsCalendarContentResponseModelContentType.eventsCalendar]: ({ content, context }) => (
    <EventsCalendarScreen content={content} context={context} />
  ),

  [ShopIntroContentResponseModelContentType.shopIntro]: ({ content }) => (
    <ShopIntroScreen content={content as ShopIntroContentResponseModel} />
  ),

  [ShopSectionContentResponseModelContentType.shopSection]: ({ content, context, fullPath }) => (
    <ShopSectionRenderer
      content={content as ShopSectionContentResponseModel}
      context={context}
      fullPath={fullPath}
    />
  ),

  [ShopDiscoverContentResponseModelContentType.shopDiscover]: ({ content, context }) => (
    <ShopDiscover content={content} context={context} />
  ),

  [ShopProductDetailContentResponseModelContentType.shopProductDetail]: ({ content, context }) => (
    <ShopProductDetailScreen content={content} context={context} />
  ),

  [ShopOrderConfirmationContentResponseModelContentType.shopOrderConfirmation]: ({ content }) => (
    <ShopOrderConfirmedScreen content={content as ShopOrderConfirmationContentResponseModel} />
  ),

  // The generic payment screen. `CheckoutScreenRouter` picks the adapter (event vs. Mews booking)
  // from router state — the `PaymentContexts` this already models on the API.
  [CheckoutContentResponseModelContentType.checkout]: ({ content, context }) => (
    <CheckoutScreenRouter content={content as CheckoutContentResponseModel} context={context} />
  ),

  // Booking flows delegate to BookingEngine which has its own internal dispatch.
  [BookIntroContentResponseModelContentType.bookIntro]: delegateToBookingEngine,
  [BookFlowEatAndDrinkContentResponseModelContentType.bookFlowEatAndDrink]: delegateToBookingEngine,
  [BookFlowSunbedContentResponseModelContentType.bookFlowSunbed]: delegateToBookingEngine,
  [BookFlowParkingContentResponseModelContentType.bookFlowParking]: delegateToBookingEngine,
  [BookFlowStayContentResponseModelContentType.bookFlowStay]: delegateToBookingEngine,
  [BookFlowExperiencesContentResponseModelContentType.bookFlowExperiences]: delegateToBookingEngine,
  [BookFlowRangeRoverContentResponseModelContentType.bookFlowRangeRover]: delegateToBookingEngine,
  [BookFlowEventContentResponseModelContentType.bookFlowEvent]: delegateToBookingEngine,
};

/**
 * Wraps `ShopSectionIntroScreen` to resolve the section slug dynamically against
 * the CMS-driven `shopSectionsRepository`. Keeps URL parsing in a single helper
 * (`getSectionFromPath`) rather than spread across screens.
 */
function ShopSectionRenderer({
  content,
  context,
  fullPath,
}: {
  content: ShopSectionContentResponseModel;
  context?: AppContext;
  fullPath?: string;
}) {
  const { slugs } = useShopSections();
  const section = getSectionFromPath(fullPath, slugs) ?? slugs[0] ?? '';
  return <ShopSectionIntroScreen content={content} section={section} context={context} />;
}

interface DefaultScreenLayoutProps {
  content: IApiContentResponseModel;
  context?: AppContext;
  mode: PageMode;
  onBack: () => void;
}

/**
 * Fallback layout for any contentType not explicitly mapped above
 * (e.g. `page`, `appScreen`, `home`, `error404`, `shopCategories`, `shopProducts`).
 * Renders a sticky `ScreenHead` plus the page's `components` block list.
 */
export function DefaultScreenLayout({ content, context, mode, onBack }: DefaultScreenLayoutProps) {
  const translation = useClientTranslation();
  // Select only `variant` (a primitive), not the whole `screenHeadOverride` object — the
  // object is replaced on every `setScreenHeadOverride` call (including no-op re-sets from
  // consumers with unstable effect deps), and subscribing to the full object here would
  // re-render this layout — and therefore its entire subtree — on every one of those calls.
  const overrideVariant = useUIStore(state => state.screenHeadOverride?.variant);
  const baseVariant = mode === PageMode.BookingDetail ? 'overlay' : 'default';
  const variant = overrideVariant ?? baseVariant;
  const isOverlay = variant === 'overlay';

  return (
    <div className="pb-safe-area pt-safe-area relative w-full">
      <div className="relative min-h-15 w-full">
        <div
          className={cn(
            'pt-safe-area fixed top-0 z-80 flex w-full',
            isOverlay ? 'bg-linear-to-b from-black/80 to-transparent backdrop-blur-[2.5px]' : 'bg-white'
          )}
        >
          <ScreenHead
            title={content?.name ?? ''}
            onBack={onBack}
            ariaLabel={translation('App.Labels.GoBack')}
            variant={baseVariant}
            className="pt-0"
          />
        </div>
      </div>
      <ModulesRow
        components={
          (content.properties as ContentPropertiesModel)?.components as ApiBlockListModel | undefined
        }
        context={context}
      />
    </div>
  );
}
